import { Expense } from "@finway-group/shared/lib/models/expense/expense.model"
import { LineItem } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { useEffect, useMemo, useState } from "react"
import { diff } from "Shared/utils/array.utils"
import { useLinkPurchaseOrderMutation } from "Shared/queries/expenses/expense.queries"
import { PurchaseOrderForLinking } from "Features/pages/purchaseOrders/types/PurchaseOrder.interfaces"
import { PurchaseOrderStatusEnum } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatus.enum"
import { InvoiceLinkRequest } from "@finway-group/shared/lib/models/expense/linkRequest.interface"

export type LinkedPair = InvoiceLinkRequest["links"][number]

const pairSortKey = (pair: LinkedPair) => `${pair.invoiceItemId},${pair.poItemId},${pair.poId}`

export const decorateItemsWithIndex = <T>(splits: Array<T>): Array<T & { index: number }> => splits?.map((item, index) => ({ ...item, index }))

export const useLinkPurchaseOrder = (expense: Expense | undefined, purchaseOrders: Array<PurchaseOrderForLinking> = []) => {
    const { mutateAsync: linkPurchaseOrder, isLoading: isLinkMutationLoading } = useLinkPurchaseOrderMutation()
    const expenseSplits = useMemo(() => decorateItemsWithIndex((expense?.splits as Array<LineItem>) || []), [expense?.splits])
    const [activeItem, setActiveItem] = useState<string>("")
    const initialLinkedPairs: Array<LinkedPair> = useMemo(
        () =>
            expenseSplits
                .filter((item) => item.purchaseOrderLink)
                .map((item) => ({
                    invoiceItemId: item._id!,
                    poId: item.purchaseOrderLink?._id!,
                    poItemId: item.purchaseOrderLink?.itemId!,
                })),
        [expenseSplits],
    )

    const isPoLinkingDisabled = (poId: string): boolean => {
        const po = purchaseOrders.find((purchaseOrder) => purchaseOrder._id === poId)
        if (!po?.state?.currentState) {
            return true
        }
        return po.state.currentState !== PurchaseOrderStatusEnum.FULFILLMENT
    }
    const [linkedPairs, setLinkedPairs] = useState<Array<LinkedPair>>([])

    useEffect(() => {
        if (initialLinkedPairs.length > 0) {
            setLinkedPairs(initialLinkedPairs)
        }
    }, [expense?.id])

    const hasChanges = useMemo(() => {
        if (linkedPairs.length !== initialLinkedPairs.length) {
            return true
        }
        const differences = diff(linkedPairs, initialLinkedPairs, pairSortKey)
        return differences.hasChanges
    }, [linkedPairs, initialLinkedPairs])

    if (expenseSplits.length === 0) {
        return {
            expenseSplits,
            handleInvoiceItemClick: () => {},
            handlePurchaseOrderItemClick: () => {},
            isPoLinkingDisabled: () => true,
            removeLink: () => {},
            clearAllLinks: () => {},
            isInvoiceItemLinked: () => false,
            isPurchaseOrderItemLinked: () => false,
            isItemActive: () => false,
            isPurchaseOrderItemDisabled: () => false,
            saveLinkChanges: () => {},
            hasChanges: false,
            linkedPairs: [],
            activeItem: null,
            isClearAllDisabled: false,
            isLinkMutationLoading: false,
        }
    }
    const handleInvoiceItemClick = (itemId: string) => {
        const isInvoiceLinked = linkedPairs.some((pair) => pair.invoiceItemId === itemId)
        if (isInvoiceLinked) {
            return
        }
        setActiveItem((prev) => (prev === itemId ? "" : itemId))
    }

    const handlePurchaseOrderItemClick = (itemId: string, poId: string) => {
        if (isPoLinkingDisabled(poId)) {
            return
        }
        const isPurchaseLinked = linkedPairs.some((pair) => pair.poId === poId && pair.poItemId === itemId)
        if (isPurchaseLinked) {
            return
        }

        if (!activeItem) {
            return
        }

        setLinkedPairs((prev) => [...prev, { invoiceItemId: activeItem, poItemId: itemId, poId }])
        setActiveItem("")
    }

    const removeLink = (invoiceItemId: string, purchaseItemId: string, poId: string) => {
        if (isPoLinkingDisabled(poId)) {
            return
        }
        setLinkedPairs((prev) => prev.filter((pair) => !(pair.invoiceItemId === invoiceItemId && pair.poItemId === purchaseItemId)))
    }

    const clearAllLinks = () => {
        // Check if any currently linked POs are completed
        const hasCompletedLinkedPO = linkedPairs.some((pair) => isPoLinkingDisabled(pair.poId))
        if (hasCompletedLinkedPO) {
            return
        }
        setLinkedPairs([])
        setActiveItem("")
    }

    const isInvoiceItemLinked = (itemId: string | undefined) => linkedPairs.some((pair) => pair.invoiceItemId === itemId)

    const isPurchaseOrderItemLinked = (poId: string, poItemId: string | undefined) => linkedPairs.some((pair) => pair.poId === poId && pair.poItemId === poItemId)

    const isItemActive = (itemId: string | undefined) => activeItem === itemId

    const isPurchaseOrderItemDisabled = (poId: string, poItemId: string | undefined) => isPoLinkingDisabled(poId) || (!activeItem && !isPurchaseOrderItemLinked(poId, poItemId))

    const saveLinkChanges = async () => {
        const differences = diff(linkedPairs, initialLinkedPairs, pairSortKey)
        if (!differences.hasChanges) {
            return
        }

        const updatedExpense = await linkPurchaseOrder({
            expenseId: expense?.id!,
            linkRequest: {
                unlinks: differences.removed,
                links: differences.added,
            },
        })
        return updatedExpense
    }

    // Helper to check if clear all should be disabled
    const isClearAllDisabled = linkedPairs.length === 0 || linkedPairs.some((pair) => isPoLinkingDisabled(pair.poId))

    return {
        expenseSplits,
        handleInvoiceItemClick,
        handlePurchaseOrderItemClick,
        removeLink,
        clearAllLinks,
        isInvoiceItemLinked,
        isPurchaseOrderItemLinked,
        isItemActive,
        isPurchaseOrderItemDisabled,
        saveLinkChanges,
        hasChanges,
        linkedPairs,
        activeItem,
        isPoLinkingDisabled,
        isClearAllDisabled,
        isLinkMutationLoading,
    }
}
